// Payment API service

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const paymentsApi = {
  // Get all available packages
  async getPackages() {
    const response = await fetch(`${API_URL}/api/packages`);
    if (!response.ok) throw new Error('Failed to fetch packages');
    return response.json();
  },

  // Get user credits
  async getUserCredits(userId, userEmail = null) {
    let url = `${API_URL}/api/user/credits/${userId}`;
    if (userEmail) {
      url += `?user_email=${encodeURIComponent(userEmail)}`;
    }
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch user credits');
    return response.json();
  },

  // Create checkout session
  async createCheckout(checkoutData) {
    const response = await fetch(`${API_URL}/api/checkout/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...checkoutData,
        origin_url: window.location.origin,
      }),
    });
    if (!response.ok) throw new Error('Failed to create checkout');
    return response.json();
  },

  // Check payment status
  async getCheckoutStatus(sessionId) {
    const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
    if (!response.ok) throw new Error('Failed to check payment status');
    return response.json();
  },

  // Validate discount code
  async validateDiscount(code, packageType, packageId) {
    const response = await fetch(`${API_URL}/api/discount/validate?code=${code}&package_type=${packageType}&package_id=${packageId}`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to validate discount');
    return response.json();
  },

  // Use calendar credit
  async useCalendarCredit(userId) {
    const response = await fetch(`${API_URL}/api/usage/calendar/${userId}`, {
      method: 'POST',
    });
    return response.json();
  },

  // Use content regeneration credit
  async useContentRegen(userId) {
    const response = await fetch(`${API_URL}/api/usage/content-regen/${userId}`, {
      method: 'POST',
    });
    return response.json();
  },

  // Use image regeneration credit
  async useImageRegen(userId) {
    const response = await fetch(`${API_URL}/api/usage/image-regen/${userId}`, {
      method: 'POST',
    });
    return response.json();
  },

  // Admin: Create discount code
  async createDiscountCode(adminEmail, codeData) {
    const response = await fetch(`${API_URL}/api/admin/discount-codes?admin_email=${adminEmail}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(codeData),
    });
    if (!response.ok) throw new Error('Failed to create discount code');
    return response.json();
  },

  // Admin: List discount codes
  async listDiscountCodes(adminEmail) {
    const response = await fetch(`${API_URL}/api/admin/discount-codes?admin_email=${adminEmail}`);
    if (!response.ok) throw new Error('Failed to fetch discount codes');
    return response.json();
  },

  // Admin: Delete discount code
  async deleteDiscountCode(adminEmail, code) {
    const response = await fetch(`${API_URL}/api/admin/discount-codes/${code}?admin_email=${adminEmail}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete discount code');
    return response.json();
  },

  // Admin: List transactions
  async listTransactions(adminEmail) {
    const response = await fetch(`${API_URL}/api/admin/transactions?admin_email=${adminEmail}`);
    if (!response.ok) throw new Error('Failed to fetch transactions');
    return response.json();
  },

  // Admin: Seed initial data (discount codes and admin credits)
  async seedAdminData(adminEmail, firebaseUid) {
    const response = await fetch(`${API_URL}/api/admin/seed-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_email: adminEmail,
        firebase_uid: firebaseUid,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to seed data');
    }
    return response.json();
  },

  // Subscription: Get customer portal URL
  async getCustomerPortalUrl(userId, returnUrl) {
    const response = await fetch(`${API_URL}/api/subscription/customer-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        return_url: returnUrl,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create portal session');
    }
    return response.json();
  },

  // Subscription: Get status
  async getSubscriptionStatus(userId) {
    const response = await fetch(`${API_URL}/api/subscription/status/${userId}`);
    if (!response.ok) throw new Error('Failed to get subscription status');
    return response.json();
  },
};
